import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useTrial } from '../contexts/TrialContext';
import { startProCheckout } from '../lib/checkout';
import { OtpVerify } from '../components/OtpVerify';
import { Eye, EyeOff, Loader2, ShieldCheck } from 'lucide-react';
import { BANNED_MESSAGE, isBannedError } from '../lib/banned';

export default function Signup() {
  const { user, signup, applyAuth } = useAuth();
  const { trialMode, price: trialPrice, cfg: trialCfg } = useTrial();
  const navigate = useNavigate();
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPw, setShowPw] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [otpEmail, setOtpEmail] = useState(''); // set once signup created the account → show OTP step
  const [otpDelivery, setOtpDelivery] = useState('');
  const [checkoutState, setCheckoutState] = useState('idle'); // idle | opening | failed

  const intent = new URLSearchParams(window.location.search).get('intent');
  const proFlow = intent === 'pro';
  // Optional post-auth destination (e.g. /signup?next=/crs-calculator).
  const nextParam = new URLSearchParams(window.location.search).get('next');
  const safeNext = nextParam && nextParam.startsWith('/') && !nextParam.startsWith('//') ? nextParam : null;

  // Already-logged-in users with NO pro intent don't belong on /signup.
  useEffect(() => {
    if (user && !proFlow) navigate(safeNext || '/dashboard', { replace: true });
  }, [user, proFlow, navigate, safeNext]);

  // A fresh FREE-intent signup must never inherit a stale pro-checkout flag —
  // otherwise onboarding would resume an old abandoned Pro checkout and send
  // a "Start Free" user to Stripe instead of their dashboard.
  useEffect(() => {
    if (!proFlow) localStorage.removeItem('pro_checkout_pending');
  }, [proFlow]);

  const openCheckout = async () => {
    setCheckoutState('opening');
    try {
      const url = await startProCheckout();
      if (url) return; // redirecting to Stripe — stop here
      setCheckoutState('failed');
    } catch {
      setCheckoutState('failed');
    }
  };

  // Trial mode (Admin → Free vs Trial): the entry tier is a paid trial, so a new account goes to the trial checkout first.
  const continueFree = () => {
    localStorage.removeItem('pro_checkout_pending');
    if (trialMode) { navigate('/checkout?plan=trial'); return; }
    navigate(user?.onboarding_completed === false || !user?.onboarding_completed ? '/onboarding' : '/dashboard');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (password.length < 6) { setError('Password must be at least 6 characters'); return; }
    setError('');
    setLoading(true);
    try {
      const res = await signup(email, password, name);
      // Email OTP gate: account created, but no session until code is verified.
      if (res?.verification_required) {
        setOtpEmail(res.email || email);
        setOtpDelivery(res.otp_delivery || '');
        setLoading(false);
        return;
      }
      // Fallback (legacy path): already authenticated
      if (proFlow) {
        await openCheckout();
        return;
      }
      navigate(trialMode ? '/checkout?plan=trial' : (safeNext || '/onboarding'));
    } catch (err) {
      setError(isBannedError(err) ? BANNED_MESSAGE : (err.response?.data?.detail || 'Signup failed'));
    } finally {
      setLoading(false);
    }
  };

  const handleVerified = async (data) => {
    applyAuth(data);
    if (proFlow) {
      await openCheckout();
      return;
    }
    if (trialMode && data?.user?.trial?.status !== 'active') { navigate('/checkout?plan=trial'); return; }
    navigate(safeNext || '/onboarding');
  };

  // ── OTP verification step (after account creation, before session) ──
  if (otpEmail && !user) {
    return (
      <div className="min-h-screen bg-neutral-50 flex flex-col">
        <div className="p-4">
          <Link to="/" className="flex items-center gap-2.5">
            <img src="/logo-navbar.png" alt="CELPIPMax" className="w-9 h-9 rounded-xl" />
            <span className="font-[Manrope] font-extrabold text-lg text-slate-900">CELPIPMax</span>
          </Link>
        </div>
        <div className="flex-1 flex items-center justify-center px-4 pb-16">
          <div className="w-full max-w-md bg-white rounded-2xl shadow-sm border border-neutral-200 p-8" data-testid="signup-otp-step">
            <OtpVerify email={otpEmail} onVerified={handleVerified} delivery={otpDelivery} />
          </div>
        </div>
      </div>
    );
  }

  // ── Pro-checkout screen: shown once the account exists and intent=pro. ──
  // Also covers users who pressed BACK from Stripe checkout — they land here
  // (logged in + intent=pro) and can resume checkout instead of being
  // silently funneled into the free flow.
  if (user && proFlow) {
    return (
      <div className="min-h-screen bg-neutral-50 flex flex-col">
        <div className="p-4">
          <Link to="/" className="flex items-center gap-2.5">
            <img src="/logo-navbar.png" alt="CELPIPMax" className="w-9 h-9 rounded-xl" />
            <span className="font-[Manrope] font-extrabold text-lg text-slate-900">CELPIPMax</span>
          </Link>
        </div>
        <div className="flex-1 flex items-center justify-center px-4 pb-16">
          <div className="w-full max-w-md bg-white rounded-2xl shadow-sm border border-neutral-200 p-8 text-center" data-testid="pro-checkout-screen">
            {checkoutState === 'opening' ? (
              <>
                <Loader2 size={40} className="animate-spin text-primary mx-auto mb-4" />
                <h1 className="text-xl font-bold text-neutral-900 mb-2">Opening secure checkout…</h1>
                <p className="text-sm text-neutral-500">You'll be redirected to Stripe to complete your Pro subscription.</p>
              </>
            ) : (
              <>
                <div className="w-14 h-14 rounded-full bg-emerald-100 text-emerald-600 flex items-center justify-center mx-auto mb-4">
                  <ShieldCheck size={26} />
                </div>
                <h1 className="text-xl font-bold text-neutral-900 mb-2" data-testid="pro-checkout-title">
                  {checkoutState === 'failed' ? "Checkout didn't open" : 'Your account is ready'}
                </h1>
                <p className="text-sm text-neutral-500 mb-6">
                  {checkoutState === 'failed'
                    ? "We couldn't open the secure checkout. Please try again — you won't be charged twice."
                    : 'One last step — complete your Pro checkout to unlock everything.'}
                </p>
                <button onClick={openCheckout}
                  className="w-full py-3 bg-primary text-white font-semibold rounded-lg hover:bg-primary-700 transition mb-3"
                  data-testid="pro-checkout-retry-btn">
                  Continue to secure checkout
                </button>
                <button onClick={continueFree}
                  className="w-full py-2.5 text-sm text-neutral-500 hover:text-neutral-700 transition underline underline-offset-4"
                  data-testid="pro-checkout-continue-free-btn">
                  {trialMode ? `Start the ${trialPrice} trial instead` : 'Continue with a free account instead'}
                </button>
              </>
            )}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-neutral-50 flex flex-col">
      <div className="p-4">
        <Link to="/" className="flex items-center gap-2.5">
          <div className="w-9 h-9 rounded-xl flex items-center justify-center">
            <img src="/logo-navbar.png" alt="CELPIPMax" className="w-9 h-9" />
          </div>
          <span className="font-[Manrope] font-extrabold text-lg text-slate-900">CELPIPMax</span>
        </Link>
      </div>
      <div className="flex-1 flex items-center justify-center px-4 pb-16">
        <div className="w-full max-w-md">
          <div className="bg-white rounded-2xl shadow-sm border border-neutral-200 p-8">
            <h1 className="text-2xl font-bold text-neutral-900 mb-1" data-testid="signup-title">Create your account</h1>
            <p className="text-neutral-500 text-sm mb-6" data-testid="signup-subtitle">{proFlow ? 'Step 1 of 2 — then complete your Pro checkout' : trialMode ? (trialCfg.signup_subtitle || `Step 1 of 2 — then a ${trialPrice} trial checkout`) : "Start preparing for CELPIP today — it's free"}</p>

            {error && <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700" data-testid="signup-error">{error}</div>}

            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-neutral-700 mb-1">Full Name</label>
                <input type="text" value={name} onChange={e => setName(e.target.value)} placeholder="John Doe" required
                  className="w-full px-4 py-2.5 border border-neutral-300 rounded-lg text-sm focus:ring-2 focus:ring-primary/20 focus:border-primary outline-none transition" data-testid="signup-name" />
              </div>
              <div>
                <label className="block text-sm font-medium text-neutral-700 mb-1">Email</label>
                <input type="email" value={email} onChange={e => setEmail(e.target.value)} placeholder="you@example.com" required
                  className="w-full px-4 py-2.5 border border-neutral-300 rounded-lg text-sm focus:ring-2 focus:ring-primary/20 focus:border-primary outline-none transition" data-testid="signup-email" />
              </div>
              <div>
                <label className="block text-sm font-medium text-neutral-700 mb-1">Password</label>
                <div className="relative">
                  <input type={showPw ? 'text' : 'password'} value={password} onChange={e => setPassword(e.target.value)} placeholder="Min 6 characters" required
                    className="w-full px-4 py-2.5 border border-neutral-300 rounded-lg text-sm focus:ring-2 focus:ring-primary/20 focus:border-primary outline-none transition pr-10" data-testid="signup-password" />
                  <button type="button" onClick={() => setShowPw(!showPw)} className="absolute right-3 top-1/2 -translate-y-1/2 text-neutral-400">
                    {showPw ? <EyeOff size={16} /> : <Eye size={16} />}
                  </button>
                </div>
              </div>
              <button type="submit" disabled={loading}
                className="w-full py-2.5 bg-primary text-white font-medium rounded-lg hover:bg-primary-700 transition disabled:opacity-60" data-testid="signup-submit">
                {loading ? 'Creating account...' : 'Create account'}
              </button>
            </form>

            <div className="relative my-6">
              <div className="absolute inset-0 flex items-center"><div className="w-full border-t border-neutral-200" /></div>
              <div className="relative flex justify-center text-xs"><span className="bg-white px-3 text-neutral-400">or continue with</span></div>
            </div>

            <a href={`${process.env.REACT_APP_BACKEND_URL}/api/auth/google`}
              className="w-full flex items-center justify-center gap-2.5 py-2.5 border border-neutral-300 rounded-lg hover:bg-neutral-50 transition text-sm font-medium text-neutral-700" data-testid="google-signup-btn">
              <svg width="18" height="18" viewBox="0 0 48 48"><path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z"/><path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"/><path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z"/><path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z"/></svg>
              Sign up with Google
            </a>

            <p className="mt-6 text-center text-sm text-neutral-500">
              Already have an account? <Link to="/login" className="text-primary font-medium hover:underline" data-testid="login-link">Sign in</Link>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
